<ng-template #additionalOptionsListTemplate let-additionalUserSelectionOptions>
  @for (option of additionalUserSelectionOptions; track option.key) {
    <div
      [class.selected]="selectedAdditionalAssignment.has(option.id)"
      [class.disabled]="option.disabled"
      class="result"
    >
      <rt-group (click)="toggleAdditionalAssignment(option)" variant="horizontal">
        <rt-checkbox
          (click)="toggleAdditionalAssignment(option)"
          [checked]="selectedAdditionalAssignment.has(option.key)"
          [disabled]="option.disabled"
          [value]="$any(option.id)"
        />
        <rt-avatar
          type="icon"
          iconName="person"
          size="base"
          customIconBackgroundColor="colorAvatarGrayLight"
          customIconColor="colorIconDefault"
          margin-right-small-x
        />
        <div layout-vertical margin-left-small>
          <rt-heading>
            <rt-truncate> {{ option.heading }} </rt-truncate>
            @if (option.badge) {
              <rt-badge
                [iconName]="option.badge.icon"
                [color]="option.badge.color"
                [text]="option.badge.label"
                margin-left-small-x
              />
            }
          </rt-heading>
          <span>{{ option.subheading }}</span>
        </div>
      </rt-group>
      <rt-divider />
    </div>
  }
</ng-template>

<ng-template #listTemplate let-visibleUsers>
  @for (user of visibleUsers; track user.id) {
    <div
      [class.disabled]="disabledIds[user.id]"
      [class.selected]="selectedIds.has(user.id)"
      class="result"
      [attr.data-cy]="'admins-select-dialog-form:item:' + user.id"
    >
      <rt-group (click)="toggleAssignment(user)" variant="horizontal">
        <span
          [rtTooltipHasTextAlignCenter]="true"
          [rtTooltipMaxWidth]="245"
          [rtTooltip]="disabledIds[user.id] && lockedAssignmentsTooltip()"
        >
          <rt-checkbox
            (click)="toggleAssignment(user)"
            [checked]="selectedIds.has(user.id)"
            [disabled]="disabledIds[user.id]"
            [value]="$any(user.id)"
          />
        </span>
        <rt-avatar
          [imageSrc]="user.photoThumbUrl"
          [initials]="user.initials"
          [shape]="'oval'"
          [size]="'base'"
          [type]="user.hasAvatar ? 'image' : 'initials'"
          context="admin"
        />
        <div layout-vertical margin-left-small>
          <rt-heading>
            <rt-truncate> {{ user.firstName }} {{ user.lastName }} </rt-truncate>
          </rt-heading>
          <span>{{ user.email }}</span>
        </div>
      </rt-group>
      <rt-divider />
    </div>
  }
</ng-template>

<ng-template #disabledListTemplate let-visibleUsers>
  @for (user of visibleUsers; track user.id) {
    <div
      (click)="$event.stopImmediatePropagation()"
      class="result result__disabled"
      [attr.data-cy]="'admins-select-dialog-form:item:' + user.id"
    >
      <rt-group
        [rtTooltipMaxWidth]="260"
        [rtTooltip]="disabledUserCopy()"
        rtTooltipPosition="left"
        variant="horizontal"
      >
        <rt-checkbox [disabled]="true" />
        <rt-avatar
          [imageSrc]="user.photoThumbUrl"
          [initials]="user.initials"
          [shape]="'oval'"
          [size]="'base'"
          [type]="user.hasAvatar ? 'image' : 'initials'"
          context="admin"
        />
        <div class="admin-info" layout-vertical margin-left-small>
          <span class="heading">{{ user.firstName }} {{ user.lastName }}</span>
          <span>{{ user.email }}</span>
        </div>
      </rt-group>
      <rt-divider />
    </div>
  }
</ng-template>

<rt-dialog-header
  [heading]="heading()"
  [subheading]="showCountBeforeSubheading() ? selectedIds.size + ' ' + subheading() : subheading()"
  [stepText]="
    stepText() ||
    (inviteModeEnabled() ? ('app.candidate.admin_select.step_1_of_2' | translate) : '')
  "
/>

<rt-dialog-body>
  @if (loadingAdmins()) {
    <rt-spinner />
  }

  <div column-layout layout-full-width>
    @if (!loadingAdmins()) {
      <rt-box variant="vertical no-padding">
        <div class="search">
          <rt-form-field>
            <rt-input
              (reset)="cancelSearch()"
              [autoFocus]="true"
              [formControl]="searchControl"
              [hasClearButton]="true"
              [placeholder]="'app.common.placeholders.search.team_members.other' | translate"
              variant="rounded"
              data-cy="admins-select-dialog-form:search-input"
            >
              <rt-icon name="search" prefix />
            </rt-input>
          </rt-form-field>

          @if (!inviteModeEnabled()) {
            <rt-group class="select-options" variant="horizontal">
              <rt-button (click)="selectAll()" variant="plain">
                {{ 'app.common.all' | translate }}
              </rt-button>
              <rt-button (click)="selectNone()" variant="plain">
                {{ 'app.common.none' | translate }}
              </rt-button>
            </rt-group>
          }
        </div>

        <rt-divider variant="line" />

        @if (filteredUsersExceedVirtualScrollThreshold) {
          <div
            [bufferAmount]="10"
            [executeRefreshOutsideAngularZone]="true"
            [items]="virtualScrollItems"
            [scrollAnimationTime]="0"
            [scrollThrottlingTime]="0"
            [enableUnequalChildrenSizes]="true"
            (vsUpdate)="onVirtualScrollUpdate($event)"
            class="results results--virtual"
            virtualScroller
          >
            @if (filteredAdditionalUserSelectionOptions.length) {
              <ng-template
                [ngTemplateOutletContext]="{
                  $implicit: virtualScrollAdditionalUserSelectionOptions,
                }"
                [ngTemplateOutlet]="additionalOptionsListTemplate"
              />
            }

            <ng-template
              [ngTemplateOutletContext]="{ $implicit: virtualScrollUsers }"
              [ngTemplateOutlet]="listTemplate"
            />
            @if (!!filteredDisallowedUsers.length) {
              <div class="header" padding-lr padding-tb-small>
                <rt-heading variant="subheading">
                  {{ disabledUsersHeader() | uppercase }}
                </rt-heading>
              </div>
            }
            <ng-template
              [ngTemplateOutlet]="disabledListTemplate"
              [ngTemplateOutletContext]="{ $implicit: virtualScrollDisabledUsers }"
            />
          </div>
        }
        @if (!filteredUsersExceedVirtualScrollThreshold) {
          <div class="results">
            @if (filteredAdditionalUserSelectionOptions.length) {
              <ng-template
                [ngTemplateOutletContext]="{ $implicit: filteredAdditionalUserSelectionOptions }"
                [ngTemplateOutlet]="additionalOptionsListTemplate"
              />
            }
            <ng-template
              [ngTemplateOutletContext]="{ $implicit: filteredUsers }"
              [ngTemplateOutlet]="listTemplate"
            />
            @if (!!filteredDisallowedUsers.length) {
              <div class="header" padding-lr padding-tb-small>
                <rt-heading variant="subheading">
                  {{ disabledUsersHeader() | uppercase }}
                </rt-heading>
              </div>
            }
            <ng-template
              [ngTemplateOutlet]="disabledListTemplate"
              [ngTemplateOutletContext]="{ $implicit: filteredDisallowedUsers }"
            />
          </div>
        }

        @if (
          !filteredUsers.length &&
          !filteredDisallowedUsers.length &&
          !filteredAdditionalUserSelectionOptions.length
        ) {
          <rt-empty-state
            [icon]="'search'"
            [title]="'app.common.no_results_found' | translate"
            variant="small"
          >
            <rt-button (click)="cancelSearch()" variant="tertiary">
              {{ 'app.assignments.editor.clear_search' | translate }}
            </rt-button>
          </rt-empty-state>
          @if (withInviteTeamMembersButton()) {
            <div class="invite-copy">
              <p>{{ 'app.candidate.admin_select.invite_people_copy' | translate }}</p>
              <img src="assets/images/invite-arrow.png" height="200" />
            </div>
          }
        }
      </rt-box>
    }
    @if (withInviteTeamMembersButton()) {
      <rt-button margin-top (click)="inviteTeamMembersClick.emit()">
        <rt-icon name="add" prefix />
        {{ 'app.candidate.admin_select.invite_new_people' | translate }}
      </rt-button>
    }
  </div>
</rt-dialog-body>

<rt-dialog-footer [hasOptionalActions]="hasStepBack()">
  @if (hasStepBack()) {
    <rt-button (click)="goBack()" variant="plain" class="optional">
      <rt-icon name="arrow-back" />
      {{ 'app.common.go_back' | translate }}
    </rt-button>
  }
  @if (!loading()) {
    <rt-button (click)="cancel()" variant="plain">
      {{ 'app.common.cancel' | translate }}
    </rt-button>
  }
  <rt-button (click)="submit()" [disabled]="!isValid()" [loading]="loading()" variant="primary">
    @if (actionLabel) {
      @if (hasActionLabelCount()) {
        {{ actionLabel | translate: { count: selectedIds.size } }}
      } @else {
        {{ actionLabel | translate }}
      }
    } @else {
      {{ (inviteModeEnabled() ? 'app.common.continue' : 'app.common.save') | translate }}
    }
  </rt-button>
</rt-dialog-footer>
